import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import { RevealLines, SectionLabel, FadeUp, EASE } from "../site/motion";
import { MagneticButton } from "../site/Buttons";
import { SERVICE_OPTIONS, BUDGETS, CONTACT } from "@/lib/content";

const Field = ({ label, id, textarea, ...rest }) => {
  const Tag = textarea ? "textarea" : "input";
  return (
    <label htmlFor={id} className="group relative block">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60 transition-colors group-focus-within:text-skaris-sky">{label}</span>
      <Tag id={id} data-testid={`contact-${id}-input`} rows={textarea ? 4 : undefined}
        className="mt-2 w-full resize-none border-b border-cream/20 bg-transparent pb-3 font-display text-xl text-cream placeholder:text-cream/30 focus:outline-none md:text-2xl" {...rest} />
      <span className="absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-skaris-sky transition-transform duration-500 group-focus-within:scale-x-100" />
    </label>
  );
};

const Chips = ({ label, options, value, onToggle, testid }) => (
  <div>
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">{label}</span>
    <div className="mt-3 flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button type="button" key={o} onClick={() => onToggle(o)} data-testid={`${testid}-${o.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 font-body text-sm transition-colors duration-300 ${on ? "border-cream bg-cream text-skaris-ink" : "border-cream/20 text-cream/80 hover:border-cream/60"}`}>
            {on && <Check className="h-3.5 w-3.5" />}{o}
          </button>
        );
      })}
    </div>
  </div>
);

export const ContactForm = () => {
  const [services, setServices] = useState([]);
  const [budget, setBudget] = useState([]);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const toggle = (set) => (o) => set((v) => (v.includes(o) ? v.filter((x) => x !== o) : [...v, o]));

  const submit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const payload = {
      name: String(data.get("name") || "").trim(),
      email: String(data.get("email") || "").trim(),
      company: String(data.get("company") || "").trim(),
      phone: String(data.get("phone") || "").trim(),
      services,
      budget,
      message: String(data.get("message") || "").trim(),
    };
    setSending(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const res = await fetch(`${apiBase}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("save failed");
      setSent(true);
      toast.success("Thanks — we'll be in touch within one business day.");
    } catch {
      toast.error(`Could not save that. Email ${CONTACT.email} instead.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative rounded-[28px] border border-cream/10 bg-skaris-card p-7 md:p-12">
      <AnimatePresence initial={false}>
        {sent ? (
          <motion.div key="done" data-testid="contact-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="flex min-h-[520px] flex-col justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cream text-skaris-ink"><Check /></span>
            <h3 className="mt-8 font-display text-4xl font-semibold uppercase text-cream md:text-5xl">Message received.</h3>
            <p className="mt-4 max-w-md font-body text-cream/75">We will review your brief and reply within one business day with next steps.</p>
            <button data-testid="contact-send-another" onClick={() => setSent(false)} className="link-underline mt-8 w-fit font-mono text-xs uppercase tracking-[0.2em] text-cream">Send another →</button>
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} data-testid="contact-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-9">
            <div className="grid gap-9 md:grid-cols-2">
              <Field id="name" name="name" label="Your name" placeholder="Jane Doe" required />
              <Field id="email" name="email" label="Email" type="email" placeholder="jane@company.com" required />
              <Field id="company" name="company" label="Company" placeholder="Company name" />
              <Field id="phone" name="phone" label="Phone" type="tel" placeholder="+91" />
            </div>
            <Chips label="I'm interested in" options={SERVICE_OPTIONS} value={services} onToggle={toggle(setServices)} testid="contact-service" />
            <Chips label="Budget range" options={BUDGETS} value={budget} onToggle={toggle(setBudget)} testid="contact-budget" />
            <Field id="message" name="message" label="Tell us about the project" textarea placeholder="What are you trying to build or fix?" required />
            <MagneticButton type="submit" testid="contact-submit-button" disabled={sending}>{sending ? "Sending…" : "Send message"}</MagneticButton>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ContactInfo = ({ contact }) => {
  const rows = [
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}`, id: "email" },
    { icon: Phone, label: "Phone", value: contact.phone, href: contact.phoneHref, id: "phone" },
    { icon: MapPin, label: "Office", value: contact.address.join(", "), id: "address" },
    { icon: Clock, label: "Hours", value: contact.hours, id: "hours" },
  ];
  return (
    <div className="space-y-3">
      {rows.map((r, i) => {
        const Tag = r.href ? "a" : "div";
        return (
          <FadeUp key={r.id} delay={i * 0.06}>
            <Tag href={r.href} data-testid={`contact-info-${r.id}`} className="group flex items-start gap-5 rounded-[18px] border border-cream/10 p-5 transition-colors duration-500 hover:border-skaris-sky/50 hover:bg-skaris-navy/30">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-cream/15 text-skaris-sky transition-colors group-hover:bg-cream group-hover:text-skaris-ink"><r.icon className="h-4 w-4" /></span>
              <span className="min-w-0">
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">{r.label}</span>
                <span className="mt-1 block font-display text-lg text-cream [overflow-wrap:anywhere]">{r.value}</span>
              </span>
            </Tag>
          </FadeUp>
        );
      })}
    </div>
  );
};

export const ContactHeading = () => (
  <>
    <SectionLabel>Contact</SectionLabel>
    <RevealLines lines={["Let's talk", "about your", "build."]} delay={0.8}
      className="mt-8 font-display text-5xl font-semibold uppercase leading-[0.92] tracking-[-0.03em] text-cream sm:text-6xl lg:text-[7.5rem]"
      lineClass={(i) => (i === 2 ? "text-gradient" : "")} />
  </>
);
